import { useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { SpecialtyTypeEnum } from "@app/projects/enumerators";
import { MultiMapExportSpecialty } from "@app/projects/network-assessment/multi-map-export/interfaces";
import ProjectService from "@app/projects/projectService";

export interface MultiMapExportSpecialties {
  isError: boolean;
  isLoading: boolean;
  isSuccess: boolean;
  specialties: readonly MultiMapExportSpecialty[];
}

const EMPTY_SPECIALTIES: readonly MultiMapExportSpecialty[] = Object.freeze([]);

/**
 * A sibling route of the county map, so there is no CountyProjectContext to read
 * runConfiguration.specialtyGroups from; fetches the same groups instead.
 */
export const useMultiMapExportSpecialties = (
  projectId?: number,
  projectRunConfigurationId?: number,
): MultiMapExportSpecialties => {
  const projectService = useRef(new ProjectService()).current;

  const { data, isError, isLoading, isSuccess } = useQuery({
    enabled: !!projectId && !!projectRunConfigurationId,
    queryFn: async ({ signal }) => {
      const specialtyGroups = await projectService
        .getAdequacySpecialtyGroupsByRunConfigurationId(String(projectId), String(projectRunConfigurationId), signal);

      // The server sorts by template display order (category -> group -> name); preserve it.
      return (specialtyGroups ?? []).map<MultiMapExportSpecialty>((specialtyGroup) => ({
        code: specialtyGroup.code,
        name: specialtyGroup.name,
        specialtyType: specialtyGroup.isInformational ? SpecialtyTypeEnum.Informational : SpecialtyTypeEnum.Required,
      }));
    },
    queryKey: ["multi-map-export", "specialties", projectId, projectRunConfigurationId],
    staleTime: 5 * 60 * 1000,
  });

  return { isError, isLoading, isSuccess, specialties: data ?? EMPTY_SPECIALTIES };
};
